import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type NewProjectValues,
  type Project,
  type ProjectTaskChanges,
  createOpportunityProject,
  getOpportunityProject,
  updateProjectTask,
} from '~/services/ContractingService';

import { moveTask } from '~/utils/projectTasks';

export const projectQueryKeys = {
  all: ['projects'] as const,
  details: () => [...projectQueryKeys.all, 'detail'] as const,
  detail: (opportunityId: string) => [...projectQueryKeys.details(), opportunityId] as const,
};

export function useGetOpportunityProject(opportunityId?: string) {
  return useQuery({
    queryKey: projectQueryKeys.detail(opportunityId ?? ''),
    queryFn: ({ signal }) => getOpportunityProject(opportunityId, { signal }),
    enabled: !!opportunityId,
  });
}

export function useCreateOpportunityProject(opportunityId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ opportunityName, values }: { opportunityName: string; values: NewProjectValues }) =>
      createOpportunityProject(opportunityId, opportunityName, values),
    onSuccess: (project) => {
      queryClient.setQueryData(projectQueryKeys.detail(opportunityId), project);
    },
  });
}

function applyTaskChanges(project: Project, taskId: string, { status, beforeTaskId, assignees }: ProjectTaskChanges) {
  let tasks = status ? moveTask(project.tasks, taskId, status, beforeTaskId) : project.tasks;
  if (assignees) {
    tasks = tasks.map((task) => (task.id === taskId ? { ...task, assignees } : task));
  }
  return { ...project, tasks };
}

// `scope` runs saves in click order; only the last response replaces the board.
export function useUpdateProjectTask(opportunityId: string) {
  const queryClient = useQueryClient();
  const queryKey = projectQueryKeys.detail(opportunityId);
  const mutationKey = [...projectQueryKeys.all, 'update-task', opportunityId];

  return useMutation({
    mutationKey,
    scope: { id: `project-task:${opportunityId}` },
    mutationFn: ({ projectId, taskId, changes }: { projectId: string; taskId: string; changes: ProjectTaskChanges }) =>
      updateProjectTask(projectId, taskId, changes),
    onMutate: async ({ taskId, changes }) => {
      await queryClient.cancelQueries({ queryKey });
      const currentProject = queryClient.getQueryData<Project | null>(queryKey);
      if (currentProject) {
        queryClient.setQueryData(queryKey, applyTaskChanges(currentProject, taskId, changes));
      }
    },
    onSuccess: (project) => {
      // Skip while a later save is queued; its response replaces the board.
      if (queryClient.isMutating({ mutationKey }) === 1) {
        queryClient.setQueryData(queryKey, project);
      }
    },
    onError: () => {
      // Later saves may have succeeded, so refetch instead of rolling back.
      queryClient.invalidateQueries({ queryKey });
    },
  });
}
